import {_} from "vue-underscore";
import api from "../utils/api.js";
import { stripe } from "../../utilities/stripe";

const stripe_uri = api.stripeUri();

export const checkout = {
    namespaced: true,
    state: {
        webUrl: null,
        id: null,
        stripePaymentIntent: null,
        cartTotals: {
            subtotal: 0,
            shipping: 0,
            tax: 0,
            discount: 0,
            handling_fee: 0,
            grand_total: 0
        },
        billingAddress: {
            first_name: '',
            last_name: '',
            email: '',
            phone: '',
            state_id: '',
            country_id: '',
            address_street_1: '',
            address_street_2: '',
            address_city: '',
            address_zip: ''
        },
        shippingAddress: {
            first_name: '',
            last_name: '',
            email: '',
            phone: '',
            state_id: '',
            country_id: '',
            address_street_1: '',
            address_street_2: '',
            address_city: '',
            address_zip: ''
        },
        availableCountries: [],
        availableStates: [],
        stripeError: null,
    },
    getters: {
        getCheckoutUrl(state) {
            return state.webUrl;
        },
        getCheckoutId(state) {
            return state.id;
        },
        getStripePaymentIntent(state) {
            return state.stripePaymentIntent;
        },
        getCartTotals(state) {
            return state.cartTotals;
        },
        getCartBillingAddress(state) {
            return state.billingAddress;
        },
        getCartShippingAddress(state) {
            return state.shippingAddress;
        },
        getAvailableCountries(state) {
            return state.availableCountries;
        },
        getAvailableStates(state) {
            return state.availableStates;
        },
        getStripeError(state) {
            return state.stripeError;
        }
    },
    mutations: {
        setCheckoutUrl(state, url) {
            state.webUrl = url;
        },
        setCheckoutId(state, id) {
            state.id = id;
        },
        setStripePayment(state, payload) {
            state.stripePaymentIntent = payload.paymentIntent;
            state.stripeError = payload.error;
        },
        setCartTotals(state, payload) {
            if(payload.totals) state.cartTotals = payload.totals;
        },
        setBillingAddress(state, payload) {
            if(payload.billingAddress) state.billingAddress = payload.billingAddress;
        },
        setShippingAddress(state, payload) {
            if(payload.shippingAddress) state.shippingAddress = payload.shippingAddress;
        },
        setAvailableCountries(state, payload) {
            if(payload.countries) state.availableCountries = payload.countries;
        },
        setAvailableStates(state, payload) {
            if(payload.states) state.availableStates = payload.states;
        },
        resetState(state){
            state.webUrl = null;
            state.id = null;
        }
    },
    actions: {
        createShopifyCheckout(context, products) {
            return this.$apollo.mutate({
                //mutation ($input: CheckoutCreateInput!) {
                mutation: gql`
                  mutation {
                    checkoutCreate(input: $input) {
                        checkout {
                            id
                        }
                    }
                  }
                `,
                variables: {
                  input: {
                    lineItems: products
                  }
                }
            });
        },
        ssociateCheckoutWithCustomer(context, customerToken) {
            return this.$apollo.mutate({
                //mutation ($checkoutId: ID!, $customerAccessToken: String!) {
                mutation: gql`
                    mutation {
                        checkoutCustomerAssociateV2(checkoutId: $checkoutId, customerAccessToken: $customerToken) {
                            checkout {
                                webUrl
                            }
                        }
                    }
                `,
                variables: {
                    input: {
                    checkoutId: context.state.id,
                    customerToken,
                    }
                }
            });
        },
        createStripePaymentIntent(context, { billingAddress, shippingAddress }) {
            return api
                .post(stripe_uri + 'payment-intent', {
                    billingAddress,
                    shippingAddress
                })
                .then(response => {
                    context.commit("setStripePayment", {
                        paymentIntent: response.body.intent
                    });
                    context.commit("setCartTotals", {
                        totals: response.body.totals
                    });
                    context.commit("setBillingAddress", {
                        billingAddress: response.body.billing_address
                    });
                    context.commit("setShippingAddress", {
                        shippingAddress: response.body.shipping_address
                    });
                    context.commit("setAvailableCountries", {
                        countries: response.body.countries
                    });
                    context.commit("setAvailableStates", {
                        states: response.body.states
                    });
                })
                .catch(({ stack, message }) => {
                    if(typeof window.FirebasePlugin !== 'undefined') window.FirebasePlugin.logEvent("stripe_create_payment_intent_error", { message, stack });
                    context.dispatch('cart/fetchCartProducts', null, { root: true });
                    throw error.body.message;
                });
        },
        applyDiscountCode(context, { discountCode }) {
            return api
                .post(stripe_uri + 'apply-discount-code', {
                    discountCode
                })
                .then(response => {
                    context.commit("setStripePayment", {
                        paymentIntent: response.body.intent
                    });
                    context.commit("setCartTotals", {
                        totals: response.body.totals
                    });
                    return response.body.message;
                })
                .catch((error) => {
                    const { stack, body } = error;
                    const { message } = body;
                    if(typeof window.FirebasePlugin !== 'undefined') window.FirebasePlugin.logEvent("stripe_apply_discount_code_error", { message, stack });
                    throw message;
                });
        },
        removeDiscountCode(context) {
            return api
                .post(stripe_uri + 'remove-discount-code')
                .then(response => {
                    context.commit("setStripePayment", {
                        paymentIntent: response.body.intent
                    });
                    context.commit("setCartTotals", {
                        totals: response.body.totals
                    });
                })
                .catch((error) => {
                    const { stack, body } = error;
                    const { message } = body;
                    if(typeof window.FirebasePlugin !== 'undefined') window.FirebasePlugin.logEvent("stripe_remove_discount_code_error", { message, stack });
                    throw message;
                });
        },
        async confirmStripePayment(context, { card, billingDetails, shippingDetails }) {
            const response = await stripe.confirmCardPayment(
                context.state.stripePaymentIntent.client_secret,
                {
                  payment_method: {
                    card,
                    billing_details: billingDetails,
                  },
                  shipping: shippingDetails,
                }
            );

            context.commit('setStripePayment', response);

            return response;
        },
        saveCart(context, { billingAddress, shippingAddress }) {
            return api
                .post(stripe_uri + 'save-cart', {
                    billingAddress,
                    shippingAddress
                })
                .then(response => {
                    return response.body;
                })
                .catch(error => {
                });
        }
    }
};
